import { Checkbox, Text, Stack, Button } from "@chakra-ui/react"
import type { FC } from "react"
import { useEffect, useState } from "react"

import type { DelegateStatement, Organization } from "query/graphql"
import { useUpsertDelegateProfileMutation } from "query/graphql"
import { useMe } from "user/providers/MeProvider"
import GenericModal from "ui/components/GenericModal"
import MarkdownViewer from "common/components/MarkdownViewer"
import type { DisclaimerModalContent } from "organization/constants/org-disclamer"
import { useIdentity } from "identity/provider"

type Props = {
  isDisclaimerModalOpen: boolean
  onDisclaimerModalClose: () => void
  onSuccess?: () => void
  modalContent: DisclaimerModalContent
  organization: Organization
  delegateProfile?: Pick<
    DelegateStatement,
    | "isSeekingDelegation"
    | "issues"
    | "statement"
    | "statementSummary"
    | "isMember"
  >
  delegateProfileRefetch: () => void
}
const OrganizationDisclaimerModal: FC<Props> = ({
  isDisclaimerModalOpen,
  onDisclaimerModalClose,
  onSuccess,
  modalContent,
  organization,
  delegateProfile,
  delegateProfileRefetch,
}) => {
  const me = useMe()
  const { connectWallet } = useIdentity()

  const { mutate: upsertDelegateProfileMutation } =
    useUpsertDelegateProfileMutation({
      onSuccess: () => {
        delegateProfileRefetch()
      },
    })

  const { title, body, isMemberCheckboxLabel } = modalContent

  const isActiveDelegate = delegateProfile?.isSeekingDelegation ?? false

  const [isMember, setIsMember] = useState(true)
  const [hideDisclaimer, setHideDisclaimer] = useState(false)

  useEffect(() => {
    setIsMember(delegateProfile?.isMember ?? true)
  }, [delegateProfile])

  const handleContinueClick = () => {
    const dataToUpdate = {
      isMember,
      hideDisclaimer,
    }

    // If user is not an active delegate,
    // then update delegate profile to set active delegate as true
    if (!isActiveDelegate) {
      if (!me) {
        connectWallet()
        onDisclaimerModalClose()

        return
      }

      // Update isSeekingDelegation to true
      updateDelegateProfile({ isSeekingDelegation: true, ...dataToUpdate })
    }

    // Update delegate statement with isMember nad hideDisclaimer only
    else {
      updateDelegateProfile(dataToUpdate)
    }

    if (me) {
      onDisclaimerModalClose()

      if (onSuccess) onSuccess()
    }
  }

  const updateDelegateProfile = (data: {
    isSeekingDelegation?: boolean
    isMember?: boolean
  }) => {
    if (!me) {
      connectWallet()
      onDisclaimerModalClose()

      return
    }

    const issueIds = delegateProfile?.issues
      ? delegateProfile.issues
          .filter((issue) => issue.id)
          .map((issue) => issue.id)
      : []

    upsertDelegateProfileMutation({
      input: {
        organizationId: organization.id,
        statement: delegateProfile?.statement ?? "",
        isSeekingDelegation: delegateProfile?.isSeekingDelegation ?? true,
        statementSummary: delegateProfile?.statementSummary ?? "",
        issueIds,
        ...data,
      },
    })
  }

  const requireSignInToContinue = !isActiveDelegate && !Boolean(me)

  return (
    <GenericModal
      isOpen={isDisclaimerModalOpen}
      maxWidth="500px"
      title={title}
      onClose={onDisclaimerModalClose}
    >
      <Stack>
        <MarkdownViewer content={body} />

        <Checkbox
          colorScheme="purple"
          disabled={requireSignInToContinue}
          isChecked={!isMember}
          onChange={(e) => {
            e.stopPropagation()
            setIsMember(!e.target.checked)
          }}
        >
          <Text color="gray.700" fontSize="md">
            {isMemberCheckboxLabel}
          </Text>
        </Checkbox>

        <Checkbox
          colorScheme="purple"
          disabled={requireSignInToContinue}
          isChecked={hideDisclaimer}
          onChange={(e) => {
            e.stopPropagation()
            setHideDisclaimer(e.target.checked)
          }}
        >
          <Text color="gray.700" fontSize="md">
            Do not show again
          </Text>
        </Checkbox>

        <Button
          mt={3}
          variant="primary"
          width="100%"
          onClick={handleContinueClick}
        >
          {requireSignInToContinue ? "Sign in to continue" : "Continue"}
        </Button>
      </Stack>
    </GenericModal>
  )
}

export default OrganizationDisclaimerModal
